"use client";

import * as stylex from "@stylexjs/stylex";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useId, useRef, useState } from "react";
import { copy } from "@/copy";
import { pickerMonthIndex } from "@/domain/week";
import "@/theme/tokens.stylex";
import { colors, radii, shadows } from "@/theme/tokens.stylex";
import { Button } from "@/ui/Button";
import { dialogKeyDown, useFocusOnOpen } from "@/ui/dialogFocus";
import { IconButton } from "@/ui/IconButton";
import { MonthGrid, type MonthGridDay } from "@/ui/MonthGrid";
export type PickerMonthView = {
  key: string;
  label: string;
  leading: number;
  days: readonly MonthGridDay[];
};
type DatePickerProps = {
  months: readonly PickerMonthView[];
  selected: string;
  today: string;
  cell: number;
  onSelect: (day: string) => void;
  onToday: () => void;
  onClose: () => void;
};
const MONTH_BUTTON = 44;
const styles = {
  monthButton: {
    kzqmXN: "x187nhsf",
    kZKoxP: "xn3w4p2",
    $$css: true
  }
};
export function DatePicker({
  months,
  selected,
  today,
  cell,
  onSelect,
  onToday,
  onClose
}: DatePickerProps) {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(() => pickerMonthIndex(months, selected));
  const month = months[index];
  useFocusOnOpen(dialog);
  return <div className="xixxii4 x10a8y8t x1n327nk xrvj5dj x1ku5rj1 xe8ttls x9f619">
      <button type="button" tabIndex={-1} aria-hidden="true" onClick={onClose} className="x10l6tqk x10a8y8t xc342km x1717udv x1ebrkjs" />
      <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={dialogKeyDown(onClose)} className="x1n2onr6 xh8yej3 x17fpy1y xmz0i5r x78zum5 xdt5ytf xou54vl x1odjw0f x9f619 x1cnzs8 xdqyycr xwxc41k x350i8 xh540kj x1a11ngh xs0yqle x1a2a7pz">
        <div className="x78zum5 x1qughib x6s0dn4 x1v2ro7d x106a9eq xpdmqnj">
          <h2 id={titleId} className="x1ghz6dp xneq65o x1v94iar xrg9stw x1159mfc">
            {copy.calendar.pickDate}
          </h2>
          <div className="x78zum5 x6s0dn4 x167g77z">
            <Button variant="ghost" onClick={onToday}>
              {copy.calendar.today}
            </Button>
            <IconButton icon={X} label={copy.calendar.close} onClick={onClose} />
          </div>
        </div>
        <div className="x78zum5 x6s0dn4 x167g77z x106a9eq xpdmqnj">
          <h3 aria-live="polite" className="x1iyjqo2 xeuugli x1ghz6dp x1i1m3gp xfbjfs5 x1tup8yo x1u7k74">
            {month?.label}
          </h3>
          <IconButton icon={ChevronLeft} label={copy.calendar.previousMonth} disabled={index === 0} onClick={() => setIndex(index - 1)} xstyle={styles.monthButton} />
          <IconButton icon={ChevronRight} label={copy.calendar.nextMonth} disabled={index === months.length - 1} onClick={() => setIndex(index + 1)} xstyle={styles.monthButton} />
        </div>
        {month && <div className="x78zum5 xl56j7k">
            <MonthGrid days={month.days} leading={month.leading} weekdays={copy.calendar.weekdayLetters} selected={selected} today={today} cell={cell} onSelect={onSelect} />
          </div>}
      </div>
    </div>;
}